package com.patronat.top.features.dashboard.presentation.ownerreviews

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.dto.ReviewOwnerReplyResponse
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppNetworkImage
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.molecules.inputs.SearchInput
import com.patronat.top.ui.molecules.pagination.TablePagination
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.AvatarPlaceholderGray
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.LabelLarge
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft200
import com.patronat.top.ui.theme.MineShaft400
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.koinInject
import top.features.dashboard.generated.resources.Res
import top.features.dashboard.generated.resources.img_empty_ai_moderation

@Composable
fun OwnerReviewsScreen(
    modifier: Modifier = Modifier
) {
    val viewModel = koinInject<OwnerReviewsViewModel>()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.onEvent(OwnerReviewsEvent.Initialize)
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        SearchInput(
            query = state.searchQuery,
            onQueryChange = { query -> viewModel.onEvent(OwnerReviewsEvent.OnSearchChanged(query)) },
            placeholder = "Шукати відгук або заклад",
            modifier = Modifier.width(390.dp)
        )

        Spacer(modifier = Modifier.height(16.dp))

        when {
            state.isLoading && state.reviews.isEmpty() -> {
                Box(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = Primary600)
                }
            }
            state.isEmpty -> {
                EmptyStateContent(
                    image = painterResource(Res.drawable.img_empty_ai_moderation),
                    title = if (state.searchQuery.isBlank()) {
                        "Поки що немає відгуків."
                    } else {
                        "Нічого не знайдено."
                    },
                    description = "Коли клієнти залишать відгуки до ваших закладів, вони з'являться тут.",
                    buttonText = "",
                    onButtonClick = {},
                    buttonIcon = AppIcons.Plus,
                    buttonNeeded = false,
                    modifier = Modifier.weight(1f).fillMaxWidth()
                )
            }
            else -> {
                LazyColumn(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    contentPadding = PaddingValues(bottom = 8.dp)
                ) {
                    items(state.reviews, key = { it.id }) { review ->
                        OwnerReviewCard(
                            review = review,
                            venueName = state.venueNamesById[review.venueId],
                            replyDraft = state.replyDrafts[review.id].orEmpty(),
                            isReplyLoading = state.replyingReviewId == review.id,
                            isAnyReplyLoading = state.replyingReviewId != null,
                            onReplyChanged = { text ->
                                viewModel.onEvent(OwnerReviewsEvent.OnReplyChanged(review.id, text))
                            },
                            onSendReply = {
                                viewModel.onEvent(OwnerReviewsEvent.OnSendReplyClicked(review.id))
                            }
                        )
                    }
                }

                TablePagination(
                    currentPage = state.currentPage,
                    totalItems = state.totalCount.toInt(),
                    pageSize = state.pageSize,
                    onPageChange = { page -> viewModel.onEvent(OwnerReviewsEvent.OnPageChanged(page)) },
                    onPageSizeChange = { pageSize -> viewModel.onEvent(OwnerReviewsEvent.OnPageSizeChanged(pageSize)) },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun OwnerReviewCard(
    review: ReviewResponse,
    venueName: String?,
    replyDraft: String,
    isReplyLoading: Boolean,
    isAnyReplyLoading: Boolean,
    onReplyChanged: (String) -> Unit,
    onSendReply: () -> Unit
) {
    val photos = review.photos.mapNotNull { it.publicUrl?.takeIf(String::isNotBlank) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(BorderStroke(1.dp, MineShaft100), RoundedCornerShape(12.dp))
            .background(White, RoundedCornerShape(12.dp))
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            ReviewAuthorAvatar(
                name = review.author?.displayName,
                avatarUrl = review.author?.avatarUrl
            )

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                ReviewHeader(
                    authorName = review.author?.displayName.orEmpty().ifBlank { "Користувач" },
                    venueName = venueName ?: "Заклад",
                    createdAt = review.createdAt,
                    rating = review.rating
                )

                Spacer(modifier = Modifier.height(10.dp))

                Text(
                    text = review.text?.takeIf(String::isNotBlank) ?: "Відгук без тексту",
                    style = BodyLarge.copy(
                        color = MineShaft900,
                        lineHeight = 24.sp
                    ),
                    maxLines = 5,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        if (photos.isNotEmpty()) {
            Spacer(modifier = Modifier.height(16.dp))
            ReviewPhotosBlock(photos = photos)
        }

        Spacer(modifier = Modifier.height(16.dp))

        val ownerReply = review.ownerReply
        if (ownerReply != null) {
            OwnerReplyBlock(reply = ownerReply)
        } else {
            ReplyEditor(
                value = replyDraft,
                onValueChange = onReplyChanged,
                isLoading = isReplyLoading,
                enabled = !isAnyReplyLoading,
                onSend = onSendReply
            )
        }
    }
}

@Composable
private fun ReviewAuthorAvatar(
    name: String?,
    avatarUrl: String?
) {
    val resolvedName = name.orEmpty().ifBlank { "Користувач" }
    val initials = remember(resolvedName) { resolvedName.toInitials() }

    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(CircleShape)
            .background(AvatarPlaceholderGray),
        contentAlignment = Alignment.Center
    ) {
        if (!avatarUrl.isNullOrBlank()) {
            AppNetworkImage(
                model = avatarUrl,
                contentDescription = resolvedName,
                modifier = Modifier.fillMaxSize().clip(CircleShape),
                contentScale = ContentScale.Crop
            )
        } else {
            Text(
                text = initials,
                style = LabelLarge.copy(
                    color = White,
                    fontWeight = FontWeight.Medium
                ),
                textAlign = TextAlign.Center
            )
        }
    }
}

@Composable
private fun ReviewHeader(
    authorName: String,
    venueName: String,
    createdAt: Instant,
    rating: Int
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = authorName,
            style = LabelLarge.copy(
                color = MineShaft950,
                fontWeight = FontWeight.SemiBold
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        Text(
            text = "  ->  ",
            style = BodyMedium.copy(color = MineShaft400)
        )
        Text(
            text = venueName,
            style = BodyMedium.copy(color = MineShaft500),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }

    Spacer(modifier = Modifier.height(6.dp))

    Row(verticalAlignment = Alignment.CenterVertically) {
        val filledStars = rating.toFiveStarRating()
        repeat(5) { index ->
            Icon(
                painter = AppIcons.RatingStarFilled,
                contentDescription = null,
                tint = if (index < filledStars) Primary600 else MineShaft200,
                modifier = Modifier.size(16.dp)
            )
            Spacer(modifier = Modifier.width(2.dp))
        }
        Spacer(modifier = Modifier.width(10.dp))
        Text(
            text = createdAt.toDisplayDateTime(),
            style = BodyMedium.copy(color = MineShaft500)
        )
    }
}

@Composable
private fun ReviewPhotosBlock(photos: List<String>) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, MineShaft100, RoundedCornerShape(12.dp))
            .padding(14.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                painter = AppIcons.Photo,
                contentDescription = null,
                tint = MineShaft500,
                modifier = Modifier.size(18.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "Фото",
                style = LabelLarge.copy(color = MineShaft900)
            )
        }

        Spacer(modifier = Modifier.height(12.dp))

        Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            photos.forEach { url ->
                AppNetworkImage(
                    model = url,
                    contentDescription = "Фото відгуку",
                    modifier = Modifier
                        .size(width = 184.dp, height = 124.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(MineShaft50),
                    contentScale = ContentScale.Crop,
                    placeholder = AppIcons.VenueImagePlaceholder
                )
            }
        }
    }
}

@Composable
private fun ReplyEditor(
    value: String,
    onValueChange: (String) -> Unit,
    isLoading: Boolean,
    enabled: Boolean,
    onSend: () -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            enabled = enabled,
            textStyle = BodyLarge.copy(color = MineShaft900),
            cursorBrush = SolidColor(Primary600),
            modifier = Modifier
                .fillMaxWidth()
                .height(96.dp),
            decorationBox = { innerTextField ->
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .border(1.dp, MineShaft200, RoundedCornerShape(8.dp))
                        .background(White, RoundedCornerShape(8.dp))
                        .padding(horizontal = 16.dp, vertical = 14.dp)
                ) {
                    if (value.isBlank()) {
                        Text(
                            text = "Напишіть вашу відповідь.",
                            style = BodyLarge.copy(color = MineShaft500),
                            maxLines = 1
                        )
                    }
                    innerTextField()
                }
            }
        )

        AppButton(
            text = "Надіслати відповідь",
            onClick = onSend,
            type = ButtonType.BrownFilled,
            enabled = enabled && value.isNotBlank(),
            isLoading = isLoading,
            leadingIcon = AppIcons.Check,
            modifier = Modifier.widthIn(min = 230.dp)
        )
    }
}

@Composable
private fun OwnerReplyBlock(reply: ReviewOwnerReplyResponse) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(MineShaft50)
            .border(1.dp, MineShaft100, RoundedCornerShape(10.dp))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.Shield,
                    contentDescription = null,
                    tint = MineShaft600,
                    modifier = Modifier.size(18.dp)
                )
                Text(
                    text = "Відповідь закладу",
                    style = LabelLarge.copy(color = MineShaft950)
                )
            }
            Text(
                text = reply.repliedAt.toDisplayDateTime(),
                style = BodyMedium.copy(color = MineShaft500)
            )
        }

        Text(
            text = reply.text,
            style = BodyMedium.copy(
                color = MineShaft600,
                fontSize = 15.sp,
                lineHeight = 23.sp,
                fontWeight = FontWeight.Normal
            )
        )
    }
}

private fun String.toInitials(): String {
    return trim()
        .split(" ")
        .mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
        .take(2)
        .joinToString("")
        .ifBlank { "К" }
}

private fun Int.toFiveStarRating(): Int {
    return coerceIn(1, 10).let { (it + 1) / 2 }.coerceIn(1, 5)
}

private fun Instant.toDisplayDateTime(): String {
    val dateTime = toLocalDateTime(TimeZone.currentSystemDefault())
    val month = dateTime.monthNumber.toString().padStart(2, '0')
    val day = dateTime.dayOfMonth.toString().padStart(2, '0')
    val hour = dateTime.hour.toString().padStart(2, '0')
    val minute = dateTime.minute.toString().padStart(2, '0')
    return "${dateTime.year}-$month-$day $hour:$minute"
}
